import React, {useEffect, useState} from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Toolbar,
  Tooltip,
  Typography
} from '@material-ui/core';
import {Add, CloudDownload, Delete, Edit} from "@material-ui/icons";
import {useGoVehisService} from "../../utils/axios";
import ContactAgreementDialog from "./ContactAgreementDialog";

const ROW_COUNT = 20;

function buildParams(filters, offset, rowCount) {
  const params = {offset, rowCount};
  if (filters.email) params.email = filters.email;
  if (filters.nip) params.nip = filters.nip;
  if (filters.telemarketingConsent !== null) params.telemarketingConsent = filters.telemarketingConsent;
  if (filters.electronicConsent !== null) params.electronicConsent = filters.electronicConsent;
  if (filters.newsletterConsent !== null) params.newsletterConsent = filters.newsletterConsent;
  return params;
}

function buildFilterParams(filters) {
  const params = {};
  if (filters.email) params.email = filters.email;
  if (filters.nip) params.nip = filters.nip;
  if (filters.telemarketingConsent !== null) params.telemarketingConsent = filters.telemarketingConsent;
  if (filters.electronicConsent !== null) params.electronicConsent = filters.electronicConsent;
  if (filters.newsletterConsent !== null) params.newsletterConsent = filters.newsletterConsent;
  return params;
}

const emptyFilters = {
  email: '',
  nip: '',
  telemarketingConsent: null,
  electronicConsent: null,
  newsletterConsent: null
};

function ContactAgreementList() {
  const service = useGoVehisService();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [filters, setFilters] = useState(emptyFilters);
  const [pendingFilters, setPendingFilters] = useState(emptyFilters);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedAgreement, setSelectedAgreement] = useState(null);

  function reload(currentFilters = filters, currentPage = page) {
    service.get('/admin/contact-agreements', {
      params: buildParams(currentFilters, currentPage * ROW_COUNT, ROW_COUNT)
    }).then(res => {
      setItems(res.data.items);
      setTotal(res.data.total);
    }).catch(err => console.error('Błąd pobierania zgód:', err));
  }

  useEffect(() => {
    reload();
  }, []);

  const handleSearch = () => {
    setFilters(pendingFilters);
    setPage(0);
    reload(pendingFilters, 0);
  };

  const handleReset = () => {
    setPendingFilters(emptyFilters);
    setFilters(emptyFilters);
    setPage(0);
    reload(emptyFilters, 0);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
    reload(filters, newPage);
  };

  const handleAdd = () => {
    setSelectedAgreement(null);
    setDialogOpen(true);
  };

  const handleEdit = (agreement) => {
    setSelectedAgreement(agreement);
    setDialogOpen(true);
  };

  const handleDelete = async (email) => {
    if (!window.confirm(`Czy na pewno usunąć zgodę dla: ${email}?`)) return;
    try {
      await service.delete(`/admin/contact-agreements/${encodeURIComponent(email)}`);
      reload();
    } catch (err) {
      console.error('Błąd usuwania:', err);
      alert('Błąd podczas usuwania zgody.');
    }
  };

  const handleDialogSave = () => {
    reload();
  };

  const handleCsvExport = async () => {
    try {
      const response = await service.get('/admin/contact-agreements/csv', {
        params: buildFilterParams(filters),
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'contact-agreements.csv');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Błąd eksportu CSV:', err);
      alert('Błąd podczas eksportu CSV.');
    }
  };

  const boolChip = (value) => (
    value
      ? <Chip label="Tak" color="primary" size="small" />
      : <Chip label="Nie" color="default" size="small" />
  );

  const handleBoolFilter = (name, checked, indeterminate) => {
    let val = null;
    if (!indeterminate) val = checked;
    setPendingFilters(prev => ({...prev, [name]: val}));
  };

  const BoolFilterCheckbox = ({name, label}) => {
    const val = pendingFilters[name];
    const checked = val === true;
    const indeterminate = val === null;
    return (
      <FormControlLabel
        control={
          <Checkbox
            checked={checked}
            indeterminate={indeterminate}
            color="primary"
            onChange={(e) => {
              if (indeterminate) handleBoolFilter(name, true, false);
              else if (checked) handleBoolFilter(name, false, false);
              else handleBoolFilter(name, null, true);
            }}
          />
        }
        label={label}
      />
    );
  };

  return (
    <>
      <Toolbar>
        <Typography variant="h6" style={{flexGrow: 1}}>Zgody kontaktowe</Typography>
        <Tooltip title="Eksportuj CSV">
          <IconButton onClick={handleCsvExport} color="primary">
            <CloudDownload />
          </IconButton>
        </Tooltip>
        <Button variant="contained" color="primary" startIcon={<Add />} onClick={handleAdd}>
          Dodaj
        </Button>
      </Toolbar>

      <Box px={2} pb={1} display="flex" alignItems="center" flexWrap="wrap" style={{gap: 8}}>
        <TextField
          label="Email"
          size="small"
          variant="outlined"
          value={pendingFilters.email}
          onChange={e => setPendingFilters(prev => ({...prev, email: e.target.value}))}
        />
        <TextField
          label="NIP"
          size="small"
          variant="outlined"
          value={pendingFilters.nip}
          onChange={e => setPendingFilters(prev => ({...prev, nip: e.target.value}))}
        />
        <BoolFilterCheckbox name="telemarketingConsent" label="Telemarketing" />
        <BoolFilterCheckbox name="electronicConsent" label="Elektroniczna" />
        <BoolFilterCheckbox name="newsletterConsent" label="Newsletter" />
        <Button variant="contained" color="primary" onClick={handleSearch}>Szukaj</Button>
        <Button variant="outlined" onClick={handleReset}>Resetuj</Button>
      </Box>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>ID</TableCell>
            <TableCell>Email</TableCell>
            <TableCell>Imię</TableCell>
            <TableCell>Nazwisko</TableCell>
            <TableCell>Telefon</TableCell>
            <TableCell>NIP</TableCell>
            <TableCell>Źródło</TableCell>
            <TableCell>Telemarketing</TableCell>
            <TableCell>Elektroniczna</TableCell>
            <TableCell>Newsletter</TableCell>
            <TableCell></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map(item => (
            <TableRow key={item.id}>
              <TableCell>{item.id}</TableCell>
              <TableCell>{item.email}</TableCell>
              <TableCell>{item.firstName}</TableCell>
              <TableCell>{item.lastName}</TableCell>
              <TableCell>{item.phone}</TableCell>
              <TableCell>{item.nip}</TableCell>
              <TableCell>{item.source}</TableCell>
              <TableCell>{boolChip(item.telemarketingConsent)}</TableCell>
              <TableCell>{boolChip(item.electronicConsent)}</TableCell>
              <TableCell>{boolChip(item.newsletterConsent)}</TableCell>
              <TableCell>
                <Tooltip title="Edytuj">
                  <IconButton size="small" onClick={() => handleEdit(item)}>
                    <Edit fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Usuń">
                  <IconButton size="small" onClick={() => handleDelete(item.email)}>
                    <Delete fontSize="small" />
                  </IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={handlePageChange}
        rowsPerPage={ROW_COUNT}
        rowsPerPageOptions={[ROW_COUNT]}
        labelDisplayedRows={({from, to, count}) => `${from}–${to} z ${count}`}
      />

      <ContactAgreementDialog
        isOpen={dialogOpen}
        onClose={() => setDialogOpen(false)}
        agreement={selectedAgreement}
        onSave={handleDialogSave}
      />
    </>
  );
}

export default ContactAgreementList;
